/**
 *
 * Parses the vote meta data from a string.
 *
 * @param {string} meta - The vote meta data string.
 *
 * @return {Object|null} The parsed vote meta data or null if it doesn't exist.
 */
export const parseVoteMeta = (meta) => {
  if (!meta) {
    return null;
  }

  // Already an object, no need to parse
  if (typeof meta === 'object') {
    return meta;
  }

  return JSON.parse(meta);
};

/**
 * Retrieves the vote percentages for all options in a poll.
 *
 * @param {Object} count - The options vote counts.
 *
 * @return {Object} The vote percentages for each option.
 */
export const getVotePercentages = (count) => {
  const totalVotes = getTotalVotes(count);
  const decodedPollVotes = parseVoteMeta(count);

  if (!decodedPollVotes || totalVotes === 0) {
    return {};
  }

  return decodedPollVotes
    ? Object.entries(decodedPollVotes).reduce(
        (acc, [key, votes]) => ({
          ...acc,
          [key]: totalVotes > 0 ? (100 * votes) / totalVotes : 0,
        }),
        {},
      )
    : {};
};

/**
 * Retrieves the total vote count for a poll.
 *
 * @param {Object} count - The options vote counts.
 *
 * @return {number} The total vote count for the poll.
 */
export const getTotalVotes = (count) => {
  const votes = parseVoteMeta(count);

  if (!votes) {
    return 0;
  }

  return Object.values(votes).reduce((acc, curr) => acc + curr, 0);
};

/**
 * Retrieves the vote count for an option.
 *
 * @param {Object} count    - The options vote counts.
 * @param {number} optionId - The ID of the option.
 *
 * @return {number} The vote count for the option.
 */
export const getVoteCount = (count, optionId) => {
  const votes = parseVoteMeta(count);

  if (!votes) {
    return 0;
  }

  return votes[optionId] || 0;
};

/**
 * Retrieves the vote percentage for an option.
 *
 * @param {count}  count    - The options vote counts.
 * @param {number} optionId - The ID of the option.
 *
 * @return {number} The vote percentage for the option.
 */
export const getVotePercentage = (count, optionId) => {
  const allVotePercentages = getVotePercentages(count);
  const roundedPercentages = roundVotePercentages(allVotePercentages);

  return roundedPercentages[optionId] || 0;
};

/* eslint-disable complexity */
/**
 * Rounds the percentages of votes to match a target value.
 *
 * @param {Object} votePercentages - The original vote percentages.
 *
 * @return {Object} The rounded vote percentages.
 */
export const roundVotePercentages = (votePercentages) => {
  // Store the original keys to be used later.
  const keys = Object.keys(votePercentages);
  const values = Object.values(votePercentages);

  // Set up the initial values.
  const length = values.length;
  const newVals = [];
  const marginOfErrors = [];
  let i = values.length;
  let j = 0;
  let total = 0;
  let next;
  let factor1;
  let factor2;

  // map original values to new array.
  while (i--) {
    total += newVals[i] = Math.round(values[i]);
  }

  const change = total < 100 ? 1 : -1;

  while (total !== 100) {
    // select number that will be less affected by change determined
    // in terms of itself e.g. Incrementing 10 by 1 would mean
    // an error of 10% in relation to itself.
    for (i = 0; i < length; i++) {
      next = i === length - 1 ? 0 : i + 1;

      factor2 = errorFactor(values[next], newVals[next] + change);
      factor1 = errorFactor(values[i], newVals[i] + change);

      if (factor1 > factor2) {
        j = next;
      }
    }

    newVals[j] += change;
    total += change;
  }

  for (i = 0; i < length; i++) {
    marginOfErrors[i] = newVals[i] && Math.abs(values[i] - newVals[i]) / values[i];
  }

  for (i = 0; i < length; i++) {
    for (j = 0; j < length; j++) {
      if (j === i) continue;

      const roundUpFactor =
        errorFactor(values[i], newVals[i] + 1) + errorFactor(values[j], newVals[j] - 1);
      const roundDownFactor =
        errorFactor(values[i], newVals[i] - 1) + errorFactor(values[j], newVals[j] + 1);
      const sumMargin = marginOfErrors[i] + marginOfErrors[j];

      if (roundUpFactor < sumMargin) {
        newVals[i] = newVals[i] + 1;
        newVals[j] = newVals[j] - 1;
        marginOfErrors[i] = newVals[i] && Math.abs(values[i] - newVals[i]) / values[i];
        marginOfErrors[j] = newVals[j] && Math.abs(values[j] - newVals[j]) / values[j];
      }

      if (roundDownFactor < sumMargin) {
        newVals[i] = newVals[i] - 1;
        newVals[j] = newVals[j] + 1;
        marginOfErrors[i] = newVals[i] && Math.abs(values[i] - newVals[i]) / values[i];
        marginOfErrors[j] = newVals[j] && Math.abs(values[j] - newVals[j]) / values[j];
      }
    }
  }

  function errorFactor(oldNum, newNum) {
    return Math.abs(oldNum - newNum) / oldNum;
  }

  // Reconstruct the results with the original keys.
  const result = keys.reduce((acc, key, index) => {
    acc[key] = newVals[index];
    return acc;
  }, {});

  return result;
};
/* eslint-enable complexity */
